import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Flex,
  Text,
  Image,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
} from "@chakra-ui/react";
import { FaDollarSign } from "react-icons/fa";
import { secureFetch } from "../data/secureFetch";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ManageSubscription() {
  const query = useQuery();
  const token = query.get("token");
  const [subscription, setSubscription] = useState(null);
  const [donationAmountInput, setDonationAmountInput] = useState("");
  const [customTipInput, setCustomTipInput] = useState("");
  const [platformTip, setPlatformTip] = useState(""); // To be set based on existing subscription tip
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure(); // For the cancellation confirmation modal
  const {
    isOpen: isSuccessModalOpen,
    onOpen: onSuccessModalOpen,
    onClose: onSuccessModalClose,
  } = useDisclosure(); // For the success notification modal

  // Tip options for selection
  const tipOptions = useMemo(
    () => [
      { value: "0.09", label: "9%" },
      { value: "0.05", label: "5%" },
      { value: "0.03", label: "3%" },
      { value: "custom", label: "Custom (dollar amount)" },
    ],
    []
  );

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await secureFetch(
          `/api/subscriptions/manage?token=${token}`
        );
        if (response.ok) {
          const data = response;
          setSubscription(data);
          const donationAmountDollars = (data.donation_amount / 100).toFixed(2);
          setDonationAmountInput(donationAmountDollars);

          // Set platform tip
          const donationAmountCents = data.donation_amount;
          const platformTipAmountCents = data.platform_tip_amount;

          // Calculate the tip percentage
          const tipPercentage = platformTipAmountCents / donationAmountCents;
          const tipPercentageRounded = tipPercentage.toFixed(2);

          // Check if the tip percentage matches any preset option
          const matchingOption = tipOptions.find(
            (option) =>
              option.value !== "custom" && option.value === tipPercentageRounded
          );

          if (matchingOption) {
            setPlatformTip(matchingOption.value);
            setCustomTipInput("");
          } else {
            setPlatformTip("custom");
            setCustomTipInput((platformTipAmountCents / 100).toFixed(2));
          }
        } else {
          throw new Error("Invalid or expired token.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSubscription();
    } else {
      setError("No token provided.");
      setLoading(false);
    }
  }, [tipOptions, token]);

  // Handle tip change and custom tip amount
  const handleTipChange = (e) => {
    const value = e.target.value;
    setPlatformTip(value);
    if (value === "custom") {
      setCustomTipInput("");
    }
  };

  const handleCustomTipChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setCustomTipInput(value);
    }
  };

  // Calculate the total amount including the platform tip
  const calculateTotalDonation = () => {
    const donationAmount = parseFloat(donationAmountInput || "0");
    let tipAmount = 0;

    if (platformTip === "custom") {
      tipAmount = parseFloat(customTipInput || "0");
    } else {
      tipAmount = donationAmount * parseFloat(platformTip);
    }

    return (donationAmount + tipAmount).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const donationAmountCents = Math.round(
      parseFloat(donationAmountInput || "0") * 100
    );
    let updatedPlatformTipAmount = 0;

    if (platformTip === "custom") {
      updatedPlatformTipAmount = Math.round(
        parseFloat(customTipInput || "0") * 100
      );
    } else {
      updatedPlatformTipAmount = Math.round(
        donationAmountCents * parseFloat(platformTip)
      );
    }

    const updatedSubscription = {
      ...subscription,
      donation_amount: donationAmountCents,
      platform_tip_amount: updatedPlatformTipAmount,
    };

    try {
      const response = await secureFetch(`/api/subscriptions/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: { token, subscription: updatedSubscription },
      });

      if (response.ok) {
        const data = response;
        setSubscription(data);
        setDonationAmountInput((data.donation_amount / 100).toFixed(2));

        if (platformTip === "custom") {
          setCustomTipInput((data.platform_tip_amount / 100).toFixed(2));
        }

        // Open the success modal
        onSuccessModalOpen();
      } else {
        throw new Error("Failed to update subscription.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    onCancelModalOpen(); // Open the cancellation confirmation modal
  };

  const confirmCancelSubscription = async () => {
    setLoading(true);
    setError(null);
    onCancelModalClose(); // Close the confirmation modal

    try {
      const response = await secureFetch(`/api/subscriptions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: { token },
      });

      if (response.ok) {
        alert("Subscription canceled successfully.");
        setSubscription({ ...subscription, status: "canceled" });
      } else {
        throw new Error("Failed to cancel subscription.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  // If subscription is canceled, show a message and hide the form
  if (subscription && subscription.status === "canceled") {
    return (
      <Box p={6} pt={8} maxW="600px" mx="auto" mt={4} borderRadius="lg">
        <Alert status="warning" mb={4}>
          <AlertIcon />
          This subscription has been canceled and cannot be modified.
        </Alert>
        <Box textAlign="center">
          <Heading size="md">Subscription Canceled</Heading>
          <p>
            To start a new subscription, visit the campaign page:
            <br />
            <a
              href={`/campaign/${subscription.campaign_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to Campaign
            </a>
          </p>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {/* Larger PurelyGive Logo and Branding */}
      <Flex justifyContent="center" alignItems="center" mt={4} mb={4}>
        <Image
          src="../../logoWithTextNoBg.png"
          height={100}
          width={400}
          alt="PurelyGive Logo"
          // boxSize="80px"
        />
      </Flex>

      <Box
        p={6}
        pt={8}
        maxW="600px"
        mx="auto"
        borderWidth={{ base: "0", md: "1px" }}
        mt={4}
        borderRadius={{ base: "0", md: "lg" }}
        boxShadow={{ base: "none", md: "md" }}
      >
        {error && (
          <>
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
            <Box textAlign="center" py={4}>
              <Heading size="md" mb={2}>
                Subscription Error
              </Heading>
              <p>
                We're sorry, but we couldn't verify your subscription. Please
                check the link or contact support.
              </p>
            </Box>
          </>
        )}

        {!error && (
          <>
            <Heading mb={6}>Manage Your Subscription</Heading>
            <form onSubmit={handleSubmit}>
              <FormControl id="donation_amount" mb={4}>
                <FormLabel>Donation Amount ($)</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    color="gray.500"
                    fontSize="1.4em"
                    children={<FaDollarSign />}
                  />
                  <Input
                    type="text"
                    value={donationAmountInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d{0,2}$/.test(value)) {
                        setDonationAmountInput(value);
                      }
                    }}
                    onBlur={() => {
                      const amount = parseFloat(donationAmountInput || "0");
                      if (!isNaN(amount)) {
                        setSubscription({
                          ...subscription,
                          donation_amount: Math.round(amount * 100),
                        });
                      }
                    }}
                  />
                </InputGroup>
              </FormControl>

              <FormControl id="platformTip" mb={4}>
                <FormLabel>Platform Tip</FormLabel>
                <Select
                  id="platformTip"
                  value={platformTip}
                  onChange={handleTipChange}
                >
                  {tipOptions.map((option) =>
                    option.value === "custom" ? (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ) : (
                      <option key={option.value} value={option.value}>
                        {option.label} ($
                        {(
                          parseFloat(donationAmountInput || "0") *
                          parseFloat(option.value)
                        ).toFixed(2)}
                        )
                      </option>
                    )
                  )}
                </Select>
                {platformTip === "custom" && (
                  <InputGroup mt={2}>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.500"
                      fontSize="1.4em"
                      children={<FaDollarSign />}
                    />
                    <Input
                      type="text"
                      placeholder="Enter custom tip"
                      value={customTipInput}
                      onChange={handleCustomTipChange}
                      onBlur={() => {
                        const tip = parseFloat(customTipInput || "0");
                        if (!isNaN(tip)) {
                          setSubscription({
                            ...subscription,
                            platform_tip_amount: Math.round(tip * 100),
                          });
                        }
                      }}
                    />
                  </InputGroup>
                )}
              </FormControl>

              <Text mb={4}>
                Your total donation: ${calculateTotalDonation()}
              </Text>
              <Text mb={4} color="green.500">
                Thank you for supporting us and allowing PurelyGive to cover all
                processing fees!
              </Text>

              <Button type="submit" colorScheme="blue" mr={3}>
                Update Subscription
              </Button>

              <Button colorScheme="red" onClick={handleCancelSubscription}>
                Cancel Subscription
              </Button>
            </form>
          </>
        )}
      </Box>

      {/* Confirmation Modal for Cancel Subscription */}
      <Modal isOpen={isCancelModalOpen} onClose={onCancelModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Cancellation</ModalHeader>
          <ModalBody>
            Are you sure you want to cancel your subscription? This action
            cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={confirmCancelSubscription}>
              Confirm
            </Button>
            <Button variant="ghost" onClick={onCancelModalClose} ml={3}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Success Modal for Update Subscription */}
      <Modal isOpen={isSuccessModalOpen} onClose={onSuccessModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Subscription Updated</ModalHeader>
          <ModalBody>
            Your subscription has been updated successfully.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onSuccessModalClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Flex alignItems="center" justifyContent="center" mt={4} mb={6}>
        <Image
          src="../../logoWithTextNoBg.png"
          height={"40px"}
          width={180}
          alt="PurelyGive Logo"
          display="inline"
          // boxSize="20px"
          mr={1}
        />

        <Text fontSize="sm" color="gray.500" ml={4} mr={4}>
          |
        </Text>
        <Text fontSize="sm" color="gray.500">
          <a href="https://PurelyGive.com">Back to our website</a>
        </Text>
        <Text fontSize="sm" color="gray.500" ml={4} mr={4}>
          |
        </Text>
        <Text fontSize="sm" color="gray.500">
          Powered by Stripe
        </Text>
      </Flex>
    </>
  );
}

export default ManageSubscription;
